import React from 'react';
import { SvgIcon, SvgIconProps } from '@mui/material';

// Launch tier - Rocket ship
export const LaunchIcon: React.FC<SvgIconProps> = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M12 2C12 2 8 6 8 12c0 2.5 1 4.5 2 6l2 2 2-2c1-1.5 2-3.5 2-6 0-6-4-10-4-10z" fill="currentColor" opacity="0.15"/>
    <path d="M12 2C12 2 8 6 8 12c0 2.5 1 4.5 2 6l2 2 2-2c1-1.5 2-3.5 2-6 0-6-4-10-4-10z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="10" r="2" fill="none" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M8 12L5 15l3 1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 12l3 3-3 1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 20c0 0 1 2 2 2s2-2 2-2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </SvgIcon>
);

// Rise tier - Upward trending graph
export const RiseIcon: React.FC<SvgIconProps> = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M3 20L9 14L13 17L21 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 8h5v5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="3" cy="20" r="1.5" fill="currentColor"/>
    <circle cx="9" cy="14" r="1.5" fill="currentColor"/>
    <circle cx="13" cy="17" r="1.5" fill="currentColor"/>
    <circle cx="21" cy="8" r="1.5" fill="currentColor"/>
  </SvgIcon>
);

// Summit tier - Snow-capped mountain
export const SummitIcon: React.FC<SvgIconProps> = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M3 20L12 4L21 20H3z" fill="currentColor" opacity="0.15"/>
    <path d="M3 20L12 4L21 20H3z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 4L8.5 10L10 10.5L9 12L12 11L15 12L14 10.5L15.5 10L12 4z" fill="white" stroke="currentColor" strokeWidth="1"/>
    <path d="M12 4V2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M12 2L15 3.5L12 5" fill="currentColor" stroke="currentColor" strokeWidth="1"/>
  </SvgIcon>
);

// Free/Trial tier - Seedling
export const FreeIcon: React.FC<SvgIconProps> = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M12 22V12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M12 12C12 12 8 12 6 9C4 6 6 3 6 3C6 3 10 3 12 6" fill="currentColor" opacity="0.15"/>
    <path d="M12 12C12 12 8 12 6 9C4 6 6 3 6 3C6 3 10 3 12 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 8C12 8 16 8 18 5C20 2 18 0 18 0C18 0 14 0 12 3" fill="currentColor" opacity="0.15"/>
    <path d="M12 8C12 8 16 8 18 5C20 2 18 0 18 0C18 0 14 0 12 3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </SvgIcon>
);

// Map legacy tier names to new tier names
const mapLegacyTier = (tier: string): string => {
  const legacyMap: Record<string, string> = {
    bronze: 'launch',
    silver: 'rise',
    gold: 'summit',
    platinum: 'summit',
  };
  return legacyMap[tier?.toLowerCase()] || tier;
};

// Helper to get icon by tier name
export const getTierIcon = (tier: string): React.FC<SvgIconProps> => {
  const mappedTier = mapLegacyTier(tier);
  switch (mappedTier?.toLowerCase()) {
    case 'launch':
      return LaunchIcon;
    case 'rise':
      return RiseIcon;
    case 'summit':
      return SummitIcon;
    case 'trial':
    case 'free':
    case 'expired':
    default:
      return FreeIcon;
  }
};

// Tier colors
export const getTierColor = (tier: string): string => {
  const mappedTier = mapLegacyTier(tier);
  switch (mappedTier?.toLowerCase()) {
    case 'launch':
      return '#2E7D32'; // Green
    case 'rise':
      return '#1976D2'; // Blue
    case 'summit':
      return '#7B1FA2'; // Purple
    case 'trial':
      return '#ED6C02'; // Orange
    case 'expired':
      return '#9E9E9E'; // Gray
    default:
      return '#9E9E9E';
  }
};

// Export the mapping function for use in other components
export { mapLegacyTier };
